import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormularioRespostasModal } from "@components/Modals/Shared/FormularioRespostasModal/Index";
import { makeSelectFormularioModeloByProcessoId } from "@store/selectors/formularios/Processo/formularioAssociadoProcessoSelector";
import { selectStatusById } from "@store/selectors/solicitacaoSelector";
import CustomAntdModal from "@components/Modals/Shared/CustomAntdModal/Index";
import ButtonComp from "@components/UI/Button/Index";
import { carregarFormularioAssociadoProcesso } from '@store/slices/formularios/Processo/formularioAssociadoProcessoSlice';
import { FormularioSelectorProcessoModal } from "../FormularioSelectorProcessoModal/Index";
import { carregarFormularioRespostaProcesso } from "@store/slices/formularios/Processo/formularioRespostaProcessoSlice";
import { makeSelectFormularioRespostaBySolicitacao } from "@store/selectors/formularios/Processo/formularioRespostaProcessoSelector";

export const FormularioOptionProcessoModal = ({
    idProcesso,
    idSolicitacao,
    formularioModeloInicial,
    formularioRespostaInicial,
}) => {
    const dispatch = useDispatch();

    // Selectors específicos de processo
    const selectModelo = useMemo(
        () => makeSelectFormularioModeloByProcessoId(idProcesso),
        [idProcesso]
    );
    const formularioModelo = useSelector(selectModelo);
    const idFormularioAssociado = formularioModelo?.id;

    // busca a resposta do formulario associado ao processo e a solicitação
    const selectResposta = useMemo(
        () => makeSelectFormularioRespostaBySolicitacao(idProcesso, idSolicitacao),
        [idProcesso, idSolicitacao]
    );
    const formularioResposta = useSelector(selectResposta);

    const statusSolicitacao = useSelector(selectStatusById(idSolicitacao));

    const [selectorVisible, setSelectorVisible] = useState(false);
    const [respostasVisible, setRespostasVisible] = useState(false);
    const [optionsModalOpen, setOptionsModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState({ title: '', description: '', buttons: [] });

    // Inicializa estado Redux se vier inicial state
    useEffect(() => {
        if (!formularioModelo && formularioModeloInicial) {
            dispatch(
                carregarFormularioAssociadoProcesso({ idProcesso, formulario: formularioModeloInicial })
            );
        }
    }, [formularioModelo, formularioModeloInicial, idProcesso, dispatch]);

    useEffect(() => {
        if (!formularioResposta && formularioRespostaInicial) {
            dispatch(
                carregarFormularioRespostaProcesso({ idProcesso, idSolicitacao, resposta: formularioRespostaInicial })
            );
        }
    }, [formularioResposta, formularioRespostaInicial, idProcesso, dispatch]);

    const hasFormulario = Boolean(idFormularioAssociado);
    const formularioJaRespondido = Boolean(formularioResposta?.respostaJson);

    const handleClick = () => {
        // Cada case ajusta título, descrição e botões (com ButtonComp)
        if (statusSolicitacao === 1 && !hasFormulario) {
            setModalConfig({
                title: "Formulários",
                description: "Nenhum formulário associado",
                buttons: [
                    <ButtonComp
                        key="selecionar"
                        title="Selecionar um Formulário Modelo"
                        buttonColor="#125578"
                        style={{ width: '100%' }}
                        classnameIcon="fa-solid fa-list"
                        type="button"
                        hoverMessage="Selecionar formulário modelo"
                        onClick={() => {
                            setSelectorVisible(true);
                            setOptionsModalOpen(false);
                        }}
                    />,
                    <ButtonComp
                        key="cancel"
                        title="Cancelar"
                        buttonColor="#dc3545"
                        style={{ width: '100%' }}
                        classnameIcon="fa-solid fa-xmark"
                        type="button"
                        hoverMessage="Cancelar"
                        onClick={() => setOptionsModalOpen(false)}
                    />
                ]
            });
            setOptionsModalOpen(true);
            return;
        }

        if (statusSolicitacao === 1 && hasFormulario) {
            setModalConfig({
                title: "Formulário Associado",
                description: "Este processo já possui um formulário associado. Não é possível substituí-lo.",
                buttons: [
                    <ButtonComp
                        key="formulario"
                        title="Formulário"
                        buttonColor="#2b97be"
                        style={{ width: '100%' }}
                        classnameIcon="fa-solid fa-list"
                        type="button"
                        hoverMessage="Gerenciar o formulário associado"
                        onClick={() => {
                            setSelectorVisible(true);
                            setOptionsModalOpen(false);
                        }}
                    />,
                    <ButtonComp
                        key="cancel"
                        title="Cancelar"
                        buttonColor="#dc3545"
                        style={{ width: '100%' }}
                        classnameIcon="fa-solid fa-xmark"
                        type="button"
                        hoverMessage="Cancelar"
                        onClick={() => setOptionsModalOpen(false)}
                    />
                ]
            });
            setOptionsModalOpen(true);
            return;
        }

        if (statusSolicitacao < 4 && hasFormulario) {
            setModalConfig({
                title: "Formulário associado",
                description: "Você pode visualizar o formulário associado",
                buttons: [
                    <ButtonComp
                        key="visualizar"
                        title="Gerenciar o Formulário"
                        buttonColor="#125578"
                        style={{ width: '100%' }}
                        classnameIcon="fa-regular fa-eye"
                        type="button"
                        hoverMessage="Visualizar formulário vinculado a este item"
                        onClick={() => {
                            setSelectorVisible(true);
                            setOptionsModalOpen(false);
                        }}
                    />,
                    <ButtonComp
                        key="cancel"
                        title="Cancelar"
                        buttonColor="#dc3545"
                        style={{ width: '100%' }}
                        classnameIcon="fa-solid fa-xmark"
                        type="button"
                        hoverMessage="Cancelar"
                        onClick={() => setOptionsModalOpen(false)}
                    />
                ]
            });
            setOptionsModalOpen(true);
            return;
        }

        if (statusSolicitacao > 3 && formularioJaRespondido) {
            setModalConfig({
                title: "Formulário respondido",
                description: "Você pode visualizar as respostas enviadas",
                buttons: [
                    <ButtonComp
                        key="respostas"
                        title="Visualizar Respostas"
                        buttonColor="#125578"
                        style={{ width: '100%' }}
                        classnameIcon="fa-regular fa-file-lines"
                        type="button"
                        //hoverMessage="Visualizar respostas"
                        onClick={() => {
                            setRespostasVisible(true);
                            setOptionsModalOpen(false);
                        }}
                    />,
                    <ButtonComp
                        key="cancel"
                        title="Cancelar"
                        buttonColor="#dc3545"
                        style={{ width: '100%' }}
                        classnameIcon="fa-solid fa-xmark"
                        type="button"
                        //hoverMessage="Cancelar"
                        onClick={() => setOptionsModalOpen(false)}
                    />
                ]
            });
            setOptionsModalOpen(true);
            return;
        }

        // Caso padrão: apenas info
        setModalConfig({
            title: "Nenhuma ação disponível",
            description: "Nenhuma ação disponível para o estado atual.",
            buttons: [
                <ButtonComp
                    key="close"
                    title="Fechar"
                    buttonColor="#d33"
                    classnameIcon="fa-solid fa-xmark"
                    type="button"
                    hoverMessage="Fechar"
                    onClick={() => setOptionsModalOpen(false)}
                />
            ]
        });
        setOptionsModalOpen(true);
    };

    return (
        <div>
            <div className="d-flex justify-content-center gap-2">

                <ButtonComp
                    title="FORMULÁRIO"
                    type="button"
                    buttonColor="#2b97be"
                    classnameIcon="fa-regular fa-floppy-disk"
                    hoverMessage="Formulário vinculado ao processo"
                    onClick={handleClick}
                />

            </div>
            {/* ... botões e ícones ... */}
            <CustomAntdModal
                open={optionsModalOpen}
                onCancel={() => setOptionsModalOpen(false)}
                title={modalConfig.title}
                description={modalConfig.description}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {modalConfig.buttons}
                </div>
            </CustomAntdModal>

            <FormularioSelectorProcessoModal
                visible={selectorVisible}
                onClose={() => setSelectorVisible(false)}
                idProcesso={idProcesso}
                idFormularioAssociado={idFormularioAssociado}
                statusSolicitacao={statusSolicitacao}
            />

            <FormularioRespostasModal
                visible={respostasVisible}
                onClose={() => setRespostasVisible(false)}
                formularioId={formularioResposta?.id}
                idSolicitacao={idSolicitacao}
            />
        </div>
    );
};
